/**
 * Blog live-search overlay.
 *
 * Opened by the header Search button ([data-blog-search-trigger]); renders live
 * results from the existing blog_search AJAX endpoint into the dropdown built by
 * components/search/overlay.html.twig. Falls back to the button's href (the full
 * results page) when JS is unavailable.
 *
 * No URLs or copy are hardcoded here — both come from data-* attributes set by Twig.
 */

var DEBOUNCE_MS = 220;

function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
}

// Render highlighted text into el using DOM nodes — no innerHTML, XSS-safe.
function renderHighlighted(el, text, query) {
    el.textContent = '';
    var terms = query.trim().split(/\s+/)
        .map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); })
        .filter(Boolean);
    if (!terms.length) { el.textContent = text; return; }
    var parts = text.split(new RegExp('(' + terms.join('|') + ')', 'gi'));
    for (var i = 0; i < parts.length; i++) {
        if (!parts[i]) { continue; }
        if (i % 2 === 1) {
            var mark = document.createElement('mark');
            mark.textContent = parts[i];
            el.appendChild(mark);
        } else {
            el.appendChild(document.createTextNode(parts[i]));
        }
    }
}

// Interpolate %name% placeholders in a translated label template.
function fill(template, map) {
    return String(template).replace(/%(\w+)%/g, function (m, key) {
        return key in map ? map[key] : m;
    });
}

// Build a date formatter for the blog locale once, reused for every row.
// Returns null if the locale is unusable, so formatDate() can fall back.
function makeDateFormatter(locale) {
    try {
        return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) {
        return null;
    }
}

// Format an ISO date (YYYY-MM-DD) with a prepared formatter.
// Falls back to the raw value if the date or formatter is unusable.
function formatDate(value, formatter) {
    if (!value) { return ''; }
    var d = new Date(value + 'T00:00:00');
    if (!formatter || isNaN(d.getTime())) { return value; }
    return formatter.format(d);
}

function init() {
    var overlay = document.querySelector('[data-blog-search-overlay]');
    if (!overlay) { return; }

    var dialog    = overlay.querySelector('.blog-search-overlay__dialog');
    var input     = overlay.querySelector('.blog-search-overlay__input');
    var clearBtn  = overlay.querySelector('[data-blog-search-clear]');
    var panel     = overlay.querySelector('.blog-search-overlay__panel');
    var trending  = overlay.querySelector('[data-blog-search-trending]');
    var output    = overlay.querySelector('[data-blog-search-output]');
    var rowTpl    = overlay.querySelector('[data-blog-search-row-tpl]');
    var seeAllTpl = overlay.querySelector('[data-blog-search-see-all-tpl]');
    var emptyTpl  = overlay.querySelector('[data-blog-search-empty-tpl]');

    var searchUrl      = overlay.getAttribute('data-search-url');
    var postUrlTpl     = overlay.getAttribute('data-post-url');
    var locale         = overlay.getAttribute('data-locale') || 'en';
    var dateFormatter  = makeDateFormatter(locale);
    var minChars       = parseInt(overlay.getAttribute('data-min-chars'), 10) || 3;
    var labelCount     = overlay.getAttribute('data-label-count');
    var labelSeeAll    = overlay.getAttribute('data-label-see-all');
    var labelNoResults = overlay.getAttribute('data-label-no-results');

    var lastFocused = null;
    var debounceId  = null;
    var controller  = null;   // aborts an in-flight fetch when a newer query starts
    var activeIndex = -1;

    // Trending dates are server-rendered as ISO (YYYY-MM-DD); localise once on load
    // so they match the JS-rendered live results exactly.
    if (trending) {
        trending.querySelectorAll('[data-row-date]').forEach(function (el) {
            el.textContent = formatDate(el.textContent.trim(), dateFormatter);
        });
    }

    function resultsUrl(query) {
        return searchUrl + '?q=' + encodeURIComponent(query);
    }

    // ── Open / close ──────────────────────────────────────────────────────────
    function open() {
        if (!overlay.hasAttribute('hidden')) { return; }
        lastFocused = document.activeElement;
        overlay.removeAttribute('hidden');
        document.body.classList.add('blog-search-open');
        input.focus();
        syncState();
    }

    function close() {
        if (overlay.hasAttribute('hidden')) { return; }
        overlay.setAttribute('hidden', '');
        document.body.classList.remove('blog-search-open');
        if (controller) { controller.abort(); controller = null; }
        if (lastFocused && typeof lastFocused.focus === 'function') { lastFocused.focus(); }
    }

    // ── State: trending (short query) vs. live results ──────────────────────────
    function showPanel(show) {
        if (show) { panel.removeAttribute('hidden'); }
        else { panel.setAttribute('hidden', ''); }
        input.setAttribute('aria-expanded', show ? 'true' : 'false');
    }

    function syncState() {
        var query = input.value.trim();
        clearBtn.hidden = query.length === 0;

        if (query.length < minChars) {
            output.innerHTML = '';
            if (trending) { trending.hidden = false; }
            showPanel(!!trending);
            setItems();
            return;
        }

        if (trending) { trending.hidden = true; }
        fetchResults(query);
    }

    // ── Fetch + render ──────────────────────────────────────────────────────────
    function fetchResults(query) {
        if (controller) { controller.abort(); }
        controller = new AbortController();

        fetch(resultsUrl(query), {
            headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
            signal: controller.signal
        })
            .then(function (res) { return res.ok ? res.json() : null; })
            .then(function (data) {
                if (!data) { return; }
                if (data.count > 0) { renderResults(query, data); }
                else { renderEmpty(query, data.suggested_tags || []); }
                showPanel(true);
            })
            .catch(function () { /* aborted or network error — leave current panel */ });
    }

    function renderResults(query, data) {
        output.innerHTML = '';

        var label = document.createElement('p');
        label.className = 'blog-search-overlay__label';
        label.textContent = fill(labelCount, { count: data.count });
        output.appendChild(label);

        var list = document.createElement('div');
        list.className = 'blog-search-overlay__rows';
        data.results.forEach(function (item) {
            var row = rowTpl.content.firstElementChild.cloneNode(true);
            row.setAttribute('href', postUrlTpl.replace('slug-placeholder-token', encodeURIComponent(item.slug)));
            renderHighlighted(row.querySelector('[data-row-title]'), item.title, query);
            row.querySelector('[data-row-category]').textContent = item.category_name || '';
            row.querySelector('[data-row-date]').textContent = formatDate(item.published_at, dateFormatter);
            list.appendChild(row);
        });
        output.appendChild(list);

        var seeAll = seeAllTpl.content.firstElementChild.cloneNode(true);
        seeAll.setAttribute('href', resultsUrl(query));
        seeAll.querySelector('[data-see-all-label]').textContent = fill(labelSeeAll, { count: data.count, query: query });
        output.appendChild(seeAll);

        setItems();
    }

    function renderEmpty(query, tags) {
        output.innerHTML = '';
        var node = emptyTpl.content.firstElementChild.cloneNode(true);
        node.querySelector('[data-empty-title]').textContent = fill(labelNoResults, { query: query });

        var tagWrap = node.querySelector('[data-empty-tags]');
        tags.forEach(function (tag) {
            var a = document.createElement('a');
            a.className = 'blog-search-overlay__tag';
            a.setAttribute('href', resultsUrl(tag.name));
            a.textContent = tag.name;
            tagWrap.appendChild(a);
        });

        output.appendChild(node);
        setItems();
    }

    // ── Keyboard navigation (combobox/listbox) ──────────────────────────────────
    function currentItems() {
        var scope = (trending && !trending.hidden) ? trending : output;
        return Array.prototype.slice.call(scope.querySelectorAll('.blog-search-row, .blog-search-overlay__see-all'));
    }

    function setItems() {
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        currentItems().forEach(function (el, i) {
            el.id = 'blog-search-opt-' + i;
            el.classList.remove('is-active');
        });
    }

    function moveActive(delta) {
        var items = currentItems();
        if (!items.length) { return; }
        if (activeIndex >= 0) { items[activeIndex].classList.remove('is-active'); }
        activeIndex = (activeIndex + delta + items.length) % items.length;
        var el = items[activeIndex];
        el.classList.add('is-active');
        input.setAttribute('aria-activedescendant', el.id);
        el.scrollIntoView({ block: 'nearest' });
    }

    // ── Events ──────────────────────────────────────────────────────────────────
    document.querySelectorAll('[data-blog-search-trigger]').forEach(function (trigger) {
        trigger.addEventListener('click', function (e) {
            e.preventDefault();
            open();
        });
    });

    input.addEventListener('input', function () {
        clearTimeout(debounceId);
        debounceId = setTimeout(syncState, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') { e.preventDefault(); moveActive(1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); moveActive(-1); }
        else if (e.key === 'Enter') {
            e.preventDefault();
            var items = currentItems();
            if (activeIndex >= 0 && items[activeIndex]) { window.location.href = items[activeIndex].getAttribute('href'); }
            else if (input.value.trim().length) { window.location.href = resultsUrl(input.value.trim()); }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            close();
        }
    });

    clearBtn.addEventListener('click', function () {
        input.value = '';
        clearBtn.hidden = true;
        input.focus();
        syncState();
    });

    overlay.querySelectorAll('[data-blog-search-close]').forEach(function (el) {
        el.addEventListener('click', close);
    });

    // Keep clicks inside the dialog from bubbling to the backdrop-close handler.
    if (dialog) {
        dialog.addEventListener('click', function (e) { e.stopPropagation(); });
    }
}

module.exports = { init: init };
